import SectionLabel from '../common/SectionLabel';
import { formatCurrency, formatPercent } from '../../utils/formatting';
import { grossProfitPct, expensePct, netProfitPct, yoyGrowth } from '../../utils/calculations';

export default function AnnualPerformanceTable({ annualSummary, targets }) {
  // Color helper: green if meeting target, rose if below
  // For expense %, lower is better so the logic is inverted
  function metricColor(value, target, lowerIsBetter = false) {
    if (value == null) return '';
    if (lowerIsBetter) {
      return value <= target ? 'text-deep-teal' : 'text-rose';
    }
    return value >= target ? 'text-deep-teal' : 'text-rose';
  }

  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5 mb-6">
      <div className="flex items-center justify-between mb-3">
        <SectionLabel>Annual Performance History</SectionLabel>
        <span className="text-[11px] font-light text-[#888]">
          Targets: GP 60% | Exp 35% | Profit 25%
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead>
            <tr className="bg-navy text-white text-left">
              <th className="px-3 py-2 font-bold">Year</th>
              <th className="px-3 py-2 font-bold text-right">Revenue</th>
              <th className="px-3 py-2 font-bold text-right">Net Income</th>
              <th className="px-3 py-2 font-bold text-right">Gross Profit %</th>
              <th className="px-3 py-2 font-bold text-right">Expense %</th>
              <th className="px-3 py-2 font-bold text-right">Net Profit %</th>
              <th className="px-3 py-2 font-bold text-right">YoY Growth</th>
            </tr>
          </thead>
          <tbody>
            {annualSummary.map((row, idx) => {
              const gp = grossProfitPct(row.revenue, row.total_cogs);
              const ep = expensePct(row.revenue, row.total_expenses);
              const np = netProfitPct(row.revenue, row.net_income);
              const prior = idx > 0 ? annualSummary[idx - 1] : null;
              const growth = prior ? yoyGrowth(row.revenue, prior.revenue) : null;

              return (
                <tr
                  key={row.year}
                  className={`border-b border-gray-100 ${
                    row.projected ? 'bg-bone/50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                  }`}
                >
                  <td className="px-3 py-2 font-bold text-navy">
                    {row.year}
                    {row.projected && (
                      <span className="ml-1 text-[10px] font-light text-[#888]">(Proj)</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right">{formatCurrency(row.revenue)}</td>
                  <td className={`px-3 py-2 text-right ${row.net_income < 0 ? 'text-rose' : ''}`}>
                    {formatCurrency(row.net_income)}
                  </td>
                  <td className={`px-3 py-2 text-right ${metricColor(gp, targets.gross_profit_pct)}`}>
                    {formatPercent(gp)}
                  </td>
                  <td className={`px-3 py-2 text-right ${metricColor(ep, targets.operating_expense_pct, true)}`}>
                    {formatPercent(ep)}
                  </td>
                  <td className={`px-3 py-2 text-right ${metricColor(np, targets.net_profit_pct)}`}>
                    {formatPercent(np)}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {growth != null ? (
                      <span className={growth >= 0 ? 'text-deep-teal' : 'text-rose'}>
                        {growth >= 0 ? '+' : ''}{formatPercent(growth)}
                      </span>
                    ) : (
                      '--'
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
